import React, { useEffect, useMemo, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import { useStaticOptions } from "../context/StaticOptionsContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { useOrderSave } from "../context/OrderSaveContext";
import { showErrorToast } from "../components/toasts/ToastService";

const Talp = () => {
const { order, updateOrder } = useOrder();
const navigate = useStepNavigate();
const { staticOptions } = useStaticOptions();
const { saveOrder } = useOrderSave();
const [loading, setLoading] = useState(false);
const soleRamas = staticOptions?.sole_ramas || [];
const isOpenNosedModel = Number(order?.model_options?.is_open_nosed) === 1;

const formOrBuiltValue = useMemo(() => {
    if (order?.hmeliorder?.hmeliorder_sole_type === "forma") return 1;
    if (order?.hmeliorder?.hmeliorder_sole_type === "epitett") return 2;
    return null;
}, [order?.hmeliorder?.hmeliorder_sole_type]);

const filteredRows = useMemo(() => {
    return soleRamas.filter((row) => {
        if (formOrBuiltValue !== null && Number(row?.rmelisolerama_form_or_built) !== formOrBuiltValue) {
            return false;
        }

        if (isOpenNosedModel && Number(row?.rmelisolerama_is_for_open_nosed) === 0) {
            return false;
        }

        return true;
    });
}, [soleRamas, formOrBuiltValue, isOpenNosedModel]);

const typeOptions = useMemo(() => {
    const map = new Map();

    filteredRows.forEach((row) => {
        const typeId = String(row?.rmelisolerama_sole_type_id ?? "");
        const typeName = row?.rmelisolerama_sole_type_national || "";

        if (!typeId || !typeName) return;
        if (!map.has(typeId)) {
            map.set(typeId, { value: typeId, label: typeName });
        }
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "hu"));
}, [filteredRows]);

const selectedFromOrder = useMemo(() => {
    const selectedId = order?.hmeliorder?.hmeliorder_sole_rama_id;
    if (selectedId == null) return null;

    return (
        filteredRows.find(
            (r) => Number(r?.rmelisolerama_szam) === Number(selectedId)
        ) || null
    );
}, [filteredRows, order?.hmeliorder?.hmeliorder_sole_rama_id]);

const [selectedTypeId, setSelectedTypeId] = useState(
    selectedFromOrder ? String(selectedFromOrder.rmelisolerama_sole_type_id) : ""
);
const [selectedColorId, setSelectedColorId] = useState(
    selectedFromOrder ? String(selectedFromOrder.rmelisolerama_sole_color_id) : ""
);

useEffect(() => {
    if (!selectedFromOrder) {
        setSelectedTypeId("");
        setSelectedColorId("");
        return;
    }

    setSelectedTypeId(String(selectedFromOrder.rmelisolerama_sole_type_id));
    setSelectedColorId(String(selectedFromOrder.rmelisolerama_sole_color_id));
}, [selectedFromOrder]);

const colorOptionsForSelectedType = useMemo(() => {
    if (!selectedTypeId) return [];

    const map = new Map();

    filteredRows
        .filter((row) => String(row?.rmelisolerama_sole_type_id) === selectedTypeId)
        .forEach((row) => {
            const colorId = String(row?.rmelisolerama_sole_color_id ?? "");
            const colorName = row?.rmelisolerama_sole_color_national || "";
            if (!colorId || !colorName) return;

            if (!map.has(colorId)) {
                map.set(colorId, { value: colorId, label: colorName });
            }
        });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "hu"));
}, [filteredRows, selectedTypeId]);

const findSoleramaRow = (typeId, colorId) => {
    return (
        filteredRows.find(
            (r) =>
                String(r?.rmelisolerama_sole_type_id) === String(typeId) &&
                String(r?.rmelisolerama_sole_color_id) === String(colorId)
        ) || null
    );
};

const handleTypeSelect = (type) => {
    const nextTypeId = String(type.value);
    setSelectedTypeId(nextTypeId);
    setSelectedColorId("");

    updateOrder((prev) => ({
        ...prev,
        hmeliorder: {
            ...prev.hmeliorder,
            hmeliorder_sole_rama_id: 1
        }
    }));
};

const handleColorSelect = (color) => {
    const nextColorId = String(color.value);
    const matchedRow = findSoleramaRow(selectedTypeId, nextColorId);
    const matchedSzam = matchedRow?.rmelisolerama_szam != null ? Number(matchedRow.rmelisolerama_szam) : null;

    setSelectedColorId(nextColorId);

    updateOrder((prev) => ({
        ...prev,
        hmeliorder: {
            ...prev.hmeliorder,
            hmeliorder_sole_rama_id: matchedSzam
        }
    }));
    console.log("Selected sole rama ID:", matchedSzam);
};

const handleBack = () => {
    navigate("/TalpSarok");
};

const handleNext = async () => {
    setLoading(true);

    const updatedOrder = {
        ...order,
        hmeliorder: {
            ...order.hmeliorder,
        },
    };

    try {
        updateOrder(updatedOrder);
        const result = await saveOrder(updatedOrder);

        if (result?.success !== false) {
            if (order?.hmeliorder?.hmeliorder_sole_type === "forma") {
                navigate("/BelesTipus");
                return;
            }
            navigate("/Koztalp");
        } else {
            showErrorToast(result?.message || "Hiba történt a rendelés mentése során!");
        }
    } catch (err) {
        showErrorToast("Hiba történt a rendelés mentése során: " + (err?.message || err));
    } finally {
        setLoading(false);
    }
};

const canProceed = order?.hmeliorder?.hmeliorder_sole_rama_id > 1;

return (
    <TablerLayout title="Talp kiválasztása">
        <div className="row justify-content-center">
            <div className="col-md-10">
                <div className="card-body">
                    <h5 className="text-md font-semibold mb-3">Kérem adja meg a választani kívánt talp típust</h5>

                    {typeOptions.length === 0 ? (
                        <div className="alert alert-warning">
                            Nincs választható talp típus ehhez a modellhez.
                        </div>
                    ) : (
                        <div className="row g-3 mb-4">
                            {typeOptions.map((type) => (
                                <div className="col-12 col-sm-6 col-md-4" key={type.value}>
                                    <button
                                        type="button"
                                        className={
                                            "btn w-100 py-3 fw-semibold " +
                                            (selectedTypeId === type.value ? "btn-primary" : "btn-outline-primary text-primary border-primary bg-white")
                                        }
                                        style={{
                                                fontSize: 18,
                                                fontWeight: 600,
                                                borderWidth: 2,
                                                minHeight: 60,
                                            }}
                                        onClick={() => handleTypeSelect(type)}
                                    >
                                        {type.label}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <h5 className="text-md font-semibold mb-3">Kérem adja meg a választani kívánt színt</h5>

                    {!selectedTypeId && (
                        <div className="alert alert-info">
                            Először válasszon talp típust a színek megjelenítéséhez.
                        </div>
                    )}

                    {selectedTypeId && (
                        <div className="row g-3">
                            {colorOptionsForSelectedType.map((color) => (
                                <div className="col-12 col-sm-6 col-md-4" key={color.value}>
                                    <button
                                        type="button"
                                        className={
                                            "btn w-100 py-3 fw-semibold " +
                                            (selectedColorId === color.value ? "btn-primary" : "btn-outline-primary text-primary border-primary bg-white")
                                        }
                                        style={{
                                                fontSize: 18,
                                                fontWeight: 600,
                                                borderWidth: 2,
                                                minHeight: 60,
                                            }}
                                        onClick={() => handleColorSelect(color)}
                                    >
                                        {color.label}
                                    </button>
                                </div>
                            ))}

                            {colorOptionsForSelectedType.length === 0 && (
                                <div className="col-12">
                                    <div className="alert alert-warning mb-0">
                                        Ehhez a talp típushoz jelenleg nincs választható szín.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="card-footer d-flex justify-content-between">
                    <StepperNavButton side="left" label="Vissza" onClick={handleBack} />
                    <StepperNavButton side="right" label="Tovább" onClick={handleNext} disabled={!canProceed || loading} />
                </div>
            </div>
        </div>
    </TablerLayout>
);
};
export default Talp;